import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as applicationActions from '../actions/applicationActions';
import * as registrationActions from '../actions/registrationActions';
import * as accountTypeActions from '../actions/accountTypeActions';
import * as assetClassActions from '../actions/assetClassActions';
import * as identityActions from '../actions/identityActions';
import * as addressActions from '../actions/addressActions';
import * as tradingProfileActions from '../actions/tradingProfileActions';
import * as financialActions from '../actions/financialActions';
import DepositFunds from '../components/DepositFunds/DepositFunds';
import DepositFundsLoading from '../components/DepositFunds/DepositFundsLoading';
import Funding from '../components/DepositFunds/Funding';
import Config from '../Config';
import * as RouteNavigator from './RouteNavigator';
import * as userActions from '../actions/userActions';
import signOutUrl from '../lib/signOutUrl';
import hasAccountsOrFake from '../lib/containerHelpers/approvalStatus/approvalStatusHelper';
import pushToAnalytics from '../lib/analytics';
import AmplitudeFlags, { HUB_TRANSFERS_REDIRECT } from '../lib/amplitudeFlags';
import { aopAnalyticsSteps } from '../lib/analyticsHelper';
import { deleteCookieDomain, deleteCookie } from '../lib/cookieManager';
import { APPLICATION_ID_PARAM_KEY } from '../actions/storageKeys';

const config = new Config();
let modalTimer;

// window.location = url doesn't guarantee the browser actually navigates
// (popup/navigation blockers, an in-app webview, a bfcache restore on Back
// can all leave the SPA's in-memory state intact with the assignment having
// silently done nothing). Without a fallback, a customer in that situation
// is stuck on the spinner forever with no error and no way out.
//
// This is a LAST-RESORT net, deliberately far past the redirect chain's real
// p99. It used to be 8s, which repainted the page mid-redirect: every hop of
// ellis /auth/tradestation -> Auth0 -> ellis /callback (which pushes the
// accounts to CARMA) -> Hub is a 302, and a 302 never commits a new document,
// so THIS page stays displayed with its timers running until Hub's HTML
// finally commits. Seen on STG: ~1.7s inside ellis alone, on top of the
// Amplitude flag round-trip before it and Hub's SPA load after it. Customers
// watched AOP's funding view come back while the redirect was still in
// flight -- and by then the CARMA push had already succeeded, so it offered a
// second, redundant funding path from a stale page.
//
// The case the timer was really written for (the assignment doing nothing) is
// detected directly instead, via the pageshow/persisted bfcache signal below.
export const REDIRECT_FALLBACK_TIMEOUT_MS = 30000;

const redirectToAccountType = () => {
  RouteNavigator.push('/account-type');
};

const redirectToClientCenter = () => {
  window.location = config.clientCenterUrl;
};

const redirectToTsLearn = () => {
  window.location = config.tradestationLearnUrl;
};

const redirectToMidland = () => {
  window.open('https://midlandtrust.com/landing/tradestation', '_blank');
};

// Both deposit funds buttons route through ellis's funding step-up
// (application_id embedded, scope=funding). That request is the ONLY thing that
// posts the accounts to CARMA -- ellis's authHandlers.js needs application_id
// inside redirectTo to know what to push -- and it is also what mints a
// freshly-scoped token.
//
// The flag is the kill switch for the step-up as a whole:
//   on  -> step up, then land back in AOP at its own funding page
//   off -> no step-up at all, AOP's funding view renders locally
//
// Off is the pre-step-up behavior, byte for byte: the same view, rendered the
// same way, with no navigation. On is what production does today.
//
// A flag that will not resolve (Amplitude blocked by an ad blocker) counts as
// off. A kill switch that cannot be reached has to fail into the old behavior,
// otherwise turning it off does not turn anything off for those customers.
//
// Off is only safe while DEFER_CARMA is false, which is every environment's
// config today. Under DEFER_CARMA the step-up is the only thing that pushes the
// accounts to CARMA, so an off flag would strand the application. The two have
// to be switched together.
//
// Landing on /funding rather than /deposit-funds is deliberate -- returning to
// deposit-funds puts the customer back in front of the button they just
// clicked. application_id stays in the url on purpose: ellis-app parses it back
// out to rehydrate applicationId after the full page load, and ellis's
// getAction() needs it or it rewrites the customer onto /pending-applications.
// ellis strips the funding_stepup marker before the customer sees it.
const startFundingStepUp = (fdcnId, applicationId) => {
  const aopUrl = () => `${window.location.origin}/funding?${APPLICATION_ID_PARAM_KEY}=${applicationId}`;

  return AmplitudeFlags.resolveFeatureFlag(HUB_TRANSFERS_REDIRECT, fdcnId)
    .then((stepUp) => {
      if (!stepUp) return false;
      window.location = `${config.apiUrl}/auth/tradestation?redirectTo=${encodeURIComponent(aopUrl())}&scope=funding`;
      return true;
    })
    .catch(() => false);
};

const toClientCenterListener = (event) => {
  const { data } = event;
  if (data.logout) {
    redirectToClientCenter();
  }
};

export class DepositFundsContainer extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      shouldShowFunding: false,
      shouldInlineFunding: false,
      showLeavingTradeStationModal: false,
      modalTimeLeft: 10,
      isRedirectingToFunding: false,
    };
    this.createFundingUrl = this.createFundingUrl.bind(this);
    this.beginFunding = this.beginFunding.bind(this);
    this.showFunding = this.showFunding.bind(this);
    this.showInlineFunding = this.showInlineFunding.bind(this);
    this.startNewApplication = this.startNewApplication.bind(this);
    this.checkInlineFunding = this.checkInlineFunding.bind(this);
    this.finishApplication = this.finishApplication.bind(this);
    this.toggleShowLeavingTradeStationModal = this.toggleShowLeavingTradeStationModal.bind(this);
    this.startTimer = this.startTimer.bind(this);
    this.endTimerAndModal = this.endTimerAndModal.bind(this);
    this.handleShowLeavingTradeStationModal = this.handleShowLeavingTradeStationModal.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
  }

  componentDidMount() {
    const {
      actions,
      application,
      applicationId,
      authToken,
      registration,
      accountType,
      assetClass,
      identity,
      address,
      tradingProfile,
      financial,
    } = this.props;


    if (!application) actions.fetchApplication(applicationId, authToken);
    if (!registration) actions.fetchRegistration(applicationId, authToken);
    if (
      !registration ||
      !accountType ||
      !assetClass ||
      !identity ||
      !address ||
      !tradingProfile ||
      !financial
    ) {
      deleteCookie('pendingApplication');
      Promise.all([
        actions.fetchRegistration(applicationId, authToken),
        actions.fetchAccountType(applicationId, authToken),
        actions.fetchAssetClass(applicationId, authToken),
        actions.fetchIdentity(applicationId, authToken),
        actions.fetchAddress(applicationId, authToken),
        actions.fetchTradingProfile(applicationId, authToken),
        actions.fetchFinancial(applicationId, authToken),
      ]).then(() => {
        pushToAnalytics(aopAnalyticsSteps.THANK_YOU.name, {
          applicationId,
          authToken,
          registration,
          accountType,
          assetClass,
          address,
          identity,
          tradingProfile,
          financial,
        });
      });
    } else {
      pushToAnalytics(aopAnalyticsSteps.THANK_YOU.name, {
        applicationId,
        authToken,
        registration,
        accountType,
        assetClass,
        address,
        identity,
        tradingProfile,
        financial,
      });
    }

    window.addEventListener('message', toClientCenterListener, false);
    window.addEventListener('pageshow', this.handlePageShow, false);
  }

  componentWillUnmount() {
    window.removeEventListener('message', toClientCenterListener, false);
    window.removeEventListener('pageshow', this.handlePageShow, false);
    clearTimeout(this.redirectFallbackTimer);
  }

  // A bfcache restore is the one signal that proves the redirect did not take:
  // the SPA's in-memory state survived and this page is visibly on screen
  // again (the customer pressed Back, or the assignment was blocked outright).
  // Fall back at once there, rather than stranding them on a spinner until the
  // long safety-net timeout. A non-persisted pageshow is an ordinary load and
  // must NOT cancel a redirect that is still in flight.
  handlePageShow(event) {
    if (!event || !event.persisted) return;
    if (!this.state.isRedirectingToFunding) return;
    this.fallBackToFundingView();
  }

  fallBackToFundingView() {
    clearTimeout(this.redirectFallbackTimer);
    this.setState({
      isRedirectingToFunding: false,
      [this.pendingFallbackStateKey || 'shouldShowFunding']: true,
    });
  }

  createFundingUrl() {
    const { fundingUrl } = config;
    const {
      registration: { username },
    } = this.props;
    return `${fundingUrl}/?client_id=aop&user_id=${username}`;
  }

  // resolveFeatureFlag (inside startFundingStepUp) is a real network round-trip
  // -- isRedirectingToFunding is set immediately on click, before the flag
  // resolves, so the button gives visible feedback right away instead of
  // looking unresponsive while that call is in flight. When the flag is off
  // nothing navigates away, so the loading state has to be cleared here and the
  // funding view shown instead; armRedirectFallbackTimer is only the net for a
  // redirect that was started and never committed.
  beginFunding(fallbackStateKey) {
    const { applicationId } = this.props;
    this.setState({ isRedirectingToFunding: true });
    return startFundingStepUp(this.fdcnId(), applicationId)
      .then((redirected) => {
        if (!redirected) {
          this.setState({ isRedirectingToFunding: false, [fallbackStateKey]: true });
          return;
        }
        this.armRedirectFallbackTimer(fallbackStateKey);
      });
  }

  showFunding() {
    return this.beginFunding('shouldShowFunding');
  }

  showInlineFunding() {
    return this.beginFunding('shouldInlineFunding');
  }

  armRedirectFallbackTimer(fallbackStateKey) {
    // Recorded so handlePageShow restores the same view the click asked for:
    // showFunding and showInlineFunding land on different state keys.
    this.pendingFallbackStateKey = fallbackStateKey;
    clearTimeout(this.redirectFallbackTimer);
    this.redirectFallbackTimer = setTimeout(() => {
      this.fallBackToFundingView();
    }, REDIRECT_FALLBACK_TIMEOUT_MS);
  }

  fdcnId() {
    const { registration } = this.props;
    return registration ? registration.fdcnId : null;
  }

  startNewApplication() {
    const { authToken, userId } = this.props;
    this.props.actions
      .startApplication(userId, authToken)
      .then(redirectToAccountType)
      .catch(() => {
        this.props.actions.userLogout(this.props.authToken).then(() => {
          window.location = signOutUrl('/logout.html?manual');
        });
      });
  }

  finishApplication() {
    this.props.actions.userLogout(this.props.authToken).then(redirectToTsLearn);
  }

  toggleShowLeavingTradeStationModal() {
    this.setState({ showLeavingTradeStationModal: !this.state.showLeavingTradeStationModal });
  }

  endTimerAndModal(redirect = false) {
    if (redirect) redirectToMidland();
    clearInterval(modalTimer);
    this.toggleShowLeavingTradeStationModal();
  }

  startTimer() {
    this.setState({ modalTimeLeft: 10 });
    modalTimer = setInterval(() => {
      if (this.state.modalTimeLeft <= 0) {
        this.endTimerAndModal(true);
      } else {
        this.setState({ modalTimeLeft: this.state.modalTimeLeft - 1 });
      }
    }, 1000);
  }

  handleShowLeavingTradeStationModal() {
    this.toggleShowLeavingTradeStationModal();
    this.startTimer();
  }

  shouldShowDepositFundsAndFunding() {
    // const { application, registration } = this.props;
    const { application } = this.props;
    const { hasCarmaAccounts, hasFakeOrNoMoreBusiness, hasBypassedCarmaAccounts } = hasAccountsOrFake(application);

    if (hasCarmaAccounts && !hasFakeOrNoMoreBusiness && !hasBypassedCarmaAccounts) {
      deleteCookieDomain('ts_incomplete_app', 'tradestation.com');
    }

    return (
      hasCarmaAccounts && !hasFakeOrNoMoreBusiness && !hasBypassedCarmaAccounts
    );
  }

  checkInlineFunding() {
    const { shouldInlineFunding } = this.state;
    if ((config.inlineFunding === 'V2' || config.inlineFunding === 'V3') && shouldInlineFunding) {
      RouteNavigator.push('/funding');
    }
      return (<Funding
        {...this.props}
        fundingUrl={this.createFundingUrl()}
      />);
  }

  render() {
    const { shouldShowFunding, shouldInlineFunding, isRedirectingToFunding } = this.state;
    if (isRedirectingToFunding) return <DepositFundsLoading />;
    return this.shouldShowDepositFundsAndFunding() && ((shouldShowFunding || shouldInlineFunding) ?
      this.checkInlineFunding()
      :
      (<DepositFunds
        {...this.props}
        showFunding={this.showFunding}
        showInlineFunding={this.showInlineFunding}
        accountsCreated={this.props.application.accountsCreated}
        startNewApplication={this.startNewApplication}
        finishApplication={this.finishApplication}
        handleShowLeavingTradeStationModal={this.handleShowLeavingTradeStationModal}
        showLeavingTradeStationModal={this.state.showLeavingTradeStationModal}
        modalTimeLeft={this.state.modalTimeLeft}
        endTimerAndModal={this.endTimerAndModal}
      />)
    );
  }
}

DepositFundsContainer.propTypes = {
  authToken: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  application: PropTypes.shape(),
  registration: PropTypes.shape(),
  actions: PropTypes.shape({
    fetchApplication: PropTypes.func.isRequired,
    fetchRegistration: PropTypes.func.isRequired,
    startApplication: PropTypes.func.isRequired,
    userLogout: PropTypes.func.isRequired,
    fetchAccountType: PropTypes.func.isRequired,
    fetchAssetClass: PropTypes.func.isRequired,
    fetchIdentity: PropTypes.func.isRequired,
    fetchAddress: PropTypes.func.isRequired,
    fetchTradingProfile: PropTypes.func.isRequired,
    fetchFinancial: PropTypes.func.isRequired,
  }).isRequired,
  userId: PropTypes.number.isRequired,
  accountType: PropTypes.shape(),
  assetClass: PropTypes.shape(),
  identity: PropTypes.shape(),
  address: PropTypes.shape(),
  tradingProfile: PropTypes.shape(),
  financial: PropTypes.shape(),
};

function mapStateToProps(state) {
  return {
    application: state.application,
    applicationId: state.applicationId,
    authToken: state.authToken,
    registration: state.registration,
    userId: state.userId,
    accountType: state.accountType,
    assetClass: state.assetClass,
    identity: state.identity,
    address: state.address,
    tradingProfile: state.tradingProfile,
    financial: state.financial,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign(
      {},
      applicationActions,
      registrationActions,
      userActions,
      accountTypeActions,
      assetClassActions,
      identityActions,
      addressActions,
      tradingProfileActions,
      financialActions,
    ), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DepositFundsContainer);
